import type { ValidationIssueFamilyTargetPage } from '../../../shared/types/validationNavigation.types';
import type {
  MappingInsightTone,
  MappingScopeSummary,
} from './mappingWorkspaceModel';
import { REQUIRED_ATTRIBUTE_NAMES, hasResolvedMapping } from './mappingWorkspaceModel';
import type { ValidationWorkspaceTab } from '../../../shared/stores/usePcfValidationStore';
import type { AdvancedSettings, Attribute } from '../../../shared/types';

export type MappingWorkspaceTabIndex = 0 | 1 | 2 | 3;
export type MappingWorkspaceTabKey = ValidationWorkspaceTab;

export interface MappingSelectedNode {
  type: string;
  name?: string;
}

export interface MappingWorkspaceRowSummaryInput {
  attribute: Pick<Attribute, 'pcfAttributeName' | 'revitParam' | 'attributeType' | 'fx'>;
  insight: {
    isOverridden: boolean;
  };
}

export interface MappingWorkspaceSummary {
  total: number;
  mapped: number;
  requiredOpen: number;
  overridden: number;
}

export interface MappingWorkspaceSectionSummary {
  label: string;
  tone: MappingInsightTone;
}

export interface MappingWorkspaceSectionSummaries {
  general: MappingWorkspaceSectionSummary;
  processConditions: MappingWorkspaceSectionSummary;
  projectProperties: MappingWorkspaceSectionSummary;
  advanced: MappingWorkspaceSectionSummary;
}

export interface MappingWorkspaceNavigatorSummary {
  unresolved: number;
  overridden: number;
  scopes: number;
}

export interface MappingWorkspaceModeDescriptor {
  id: MappingWorkspaceTabIndex;
  label: string;
  shortLabel: string;
  badge: number | null;
}

interface BuildSectionSummariesArgs {
  allScopeSummary: MappingScopeSummary | undefined;
  workspaceSectionSummary: {
    processConditions: MappingWorkspaceSectionSummary;
    projectProperties: MappingWorkspaceSectionSummary;
  };
  centerOfCoordinates: AdvancedSettings['centerOfCoordinates'];
}

const REQUIRED_ATTRIBUTE_SET = new Set(REQUIRED_ATTRIBUTE_NAMES);

export const buildWorkspaceSummary = (
  rows: readonly MappingWorkspaceRowSummaryInput[]
): MappingWorkspaceSummary =>
  rows.reduce<MappingWorkspaceSummary>(
    (summary, row) => ({
      total: summary.total + 1,
      mapped: summary.mapped + (hasResolvedMapping(row.attribute as Attribute) ? 1 : 0),
      requiredOpen:
        summary.requiredOpen +
        (REQUIRED_ATTRIBUTE_SET.has(row.attribute.pcfAttributeName as (typeof REQUIRED_ATTRIBUTE_NAMES)[number]) &&
        !hasResolvedMapping(row.attribute as Attribute)
          ? 1
          : 0),
      overridden: summary.overridden + (row.insight.isOverridden ? 1 : 0),
    }),
    { total: 0, mapped: 0, requiredOpen: 0, overridden: 0 }
  );

export const buildSectionSummaries = ({
  allScopeSummary,
  workspaceSectionSummary,
  centerOfCoordinates,
}: BuildSectionSummariesArgs): MappingWorkspaceSectionSummaries => ({
  general: allScopeSummary?.requiredOpen
    ? { label: `${allScopeSummary.requiredOpen} unresolved`, tone: 'warning' }
    : { label: `${allScopeSummary?.mapped ?? 0}/${allScopeSummary?.total ?? 0}`, tone: 'success' },
  processConditions: workspaceSectionSummary.processConditions,
  projectProperties: workspaceSectionSummary.projectProperties,
  advanced: {
    label: centerOfCoordinates === 'SurveyPoint' ? 'Survey' : 'Project base',
    tone: 'info',
  },
});

export const buildWorkspaceNavigatorSummary = (
  scopeCount: number,
  workspaceSummary: MappingWorkspaceSummary
): MappingWorkspaceNavigatorSummary => ({
  unresolved: workspaceSummary.requiredOpen,
  overridden: workspaceSummary.overridden,
  scopes: scopeCount,
});

export const buildWorkspaceModeDescriptors = (
  requiredOpen: number
): MappingWorkspaceModeDescriptor[] => [
  {
    id: 0,
    label: 'Mapping workspace',
    shortLabel: 'Mapping',
    badge: requiredOpen > 0 ? requiredOpen : null,
  },
  {
    id: 1,
    label: 'Quick Fill properties',
    shortLabel: 'Quick Fill',
    badge: null,
  },
  {
    id: 2,
    label: 'PCF validation',
    shortLabel: 'Validation',
    badge: null,
  },
  {
    id: 3,
    label: 'Export PCF',
    shortLabel: 'Export',
    badge: null,
  },
];

export const getActiveValidationTargetPages = (
  activeTab: MappingWorkspaceTabIndex
): ValidationIssueFamilyTargetPage[] => {
  switch (activeTab) {
    case 0:
      return ['mapping'];
    case 1:
      return ['quick-fill'];
    case 2:
      return ['validation'];
    default:
      return [];
  }
};

export const shouldShowWorkspaceValidationNotice = (
  activeTab: MappingWorkspaceTabIndex
): boolean => activeTab !== 1;

export const resolveRequestedWorkspaceTab = (
  requestedWorkspaceTab: ValidationWorkspaceTab | null | undefined
): MappingWorkspaceTabIndex | null => {
  switch (requestedWorkspaceTab) {
    case 'mapping':
      return 0;
    case 'quickFill':
      return 1;
    case 'validation':
      return 2;
    case 'export':
      return 3;
    default:
      return null;
  }
};

export const resolveInitialMappingWorkspaceTab = (
  search: string | undefined
): MappingWorkspaceTabIndex => {
  const requestedTabKey = resolveInitialMappingWorkspaceTabKey(search);
  return toMappingWorkspaceTabIndex(
    requestedTabKey === 'quickFill'
      ? 1
      : requestedTabKey === 'validation'
        ? 2
        : requestedTabKey === 'export'
          ? 3
          : 0
  );
};

export const resolveInitialMappingWorkspaceTabKey = (
  search: string | undefined
): MappingWorkspaceTabKey | null => {
  try {
    const params = new URLSearchParams(search || '');
    const requestedTab = (params.get('startTab') || params.get('tab') || '').trim().toLowerCase();

    if (requestedTab === 'export' || requestedTab === 'systems') {
      return 'export';
    }

    if (requestedTab === 'validation') {
      return 'validation';
    }

    if (requestedTab === 'quickfill') {
      return 'quickFill';
    }
  } catch {
    return null;
  }

  return null;
};

export const toMappingWorkspaceTabIndex = (value: number): MappingWorkspaceTabIndex => {
  switch (value) {
    case 1:
      return 1;
    case 2:
      return 2;
    case 3:
      return 3;
    default:
      return 0;
  }
};

export const toMappingWorkspaceTabKey = (
  value: MappingWorkspaceTabIndex
): MappingWorkspaceTabKey => {
  switch (value) {
    case 1:
      return 'quickFill';
    case 2:
      return 'validation';
    case 3:
      return 'export';
    default:
      return 'mapping';
  }
};
